export const ROUTE_SURFACE_DARK = "#141414";
export const ROUTE_SURFACE_LIGHT = "#f4f6f8";

export type RouteSurfaceMode = "dark" | "light" | "login";

type RouteSurface = {
  mode: RouteSurfaceMode;
  color: string;
};

let loginSurfaceLocks = 0;

function viteBasePath(): string {
  const raw = import.meta.env.BASE_URL;
  if (!raw || raw === "/") return "";
  return raw.replace(/\/+$/, "");
}

export function normalizeRoutePath(pathname: string): string {
  let path = (pathname || "/").split(/[?#]/, 1)[0] || "/";
  const base = viteBasePath();

  if (base && path === base) {
    path = "/";
  } else if (base && path.startsWith(`${base}/`)) {
    path = path.slice(base.length) || "/";
  }

  if (!path.startsWith("/")) path = `/${path}`;
  return path.replace(/\/+$/, "") || "/";
}

export function isDarkRouteSurface(pathname: string): boolean {
  const path = normalizeRoutePath(pathname);
  return (
    path === "/login" ||
    path.startsWith("/login/") ||
    path === "/entrar" ||
    path.startsWith("/entrar/") ||
    path === "/acessar" ||
    path.startsWith("/acessar/") ||
    path === "/auth" ||
    path.startsWith("/auth/") ||
    path === "/autenticacao" ||
    path.startsWith("/autenticacao/") ||
    path === "/caixa" ||
    path.startsWith("/caixa/") ||
    path === "/kds" ||
    path.startsWith("/kds/") ||
    path === "/comandas" ||
    path.startsWith("/comandas/") ||
    path === "/garcom" ||
    path.startsWith("/garcom/") ||
    path === "/cardapio" ||
    path.startsWith("/cardapio/")
  );
}

function applyRouteSurface(surface: RouteSurface): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  root.dataset.appSurface = surface.mode;
  root.style.setProperty("--app-surface-bg", surface.color);

  let themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!themeMeta) {
    themeMeta = document.createElement("meta");
    themeMeta.name = "theme-color";
    document.head.appendChild(themeMeta);
  }
  themeMeta.content = surface.color;
}

export function routeSurfaceForPath(pathname: string): RouteSurface {
  const dark = isDarkRouteSurface(pathname);
  return {
    mode: dark ? "dark" : "light",
    color: dark ? ROUTE_SURFACE_DARK : ROUTE_SURFACE_LIGHT,
  };
}

export function applyRouteSurfaceForPath(pathname: string): void {
  if (loginSurfaceLocks > 0) {
    applyLoginSurface();
    return;
  }
  applyRouteSurface(routeSurfaceForPath(pathname));
}

export function applyLoginSurface(): void {
  applyRouteSurface({
    mode: "login",
    color: ROUTE_SURFACE_DARK,
  });
}

export function restoreRouteSurfaceFromLocation(): void {
  if (typeof window === "undefined") return;
  applyRouteSurfaceForPath(window.location.pathname);
}

export function activateLoginSurface(): () => void {
  loginSurfaceLocks += 1;
  applyLoginSurface();

  return () => {
    loginSurfaceLocks = Math.max(0, loginSurfaceLocks - 1);
    if (loginSurfaceLocks > 0) {
      applyLoginSurface();
      return;
    }
    restoreRouteSurfaceFromLocation();
  };
}
